import React, { useCallback, useMemo } from 'react';
import Pill from '../interaction/Pill';
import RolesGrid from './RolesGrid';

/**
 * The two roles sections of one user card, split so each can fold on its own:
 *
 *   UserRolesSummary — what this user has, plus the Quick add shortcuts
 *   UserRolesCatalog — the full picker, one card per role type
 *
 * Both drive the same draft.roles, so a pick in the catalog shows up in the
 * summary immediately and vice versa.
 *
 * Both are memoised and take only primitives and stable references as props.
 * Nothing here depends on the email or display-name fields, so typing in them
 * must not rebuild these pills.
 */

// Shared by both sections: turn a click on a role into the next roles array.
function useToggleRole(draftKey, selectedRoles, onRolesChange) {
  return useCallback((roleId) => {
    onRolesChange(
      draftKey,
      selectedRoles.includes(roleId)
        ? selectedRoles.filter(id => id !== roleId)
        : [...selectedRoles, roleId]
    );
  }, [draftKey, selectedRoles, onRolesChange]);
}

export const UserRolesSummary = React.memo(function UserRolesSummary({
  draftKey,
  hasRoles,
  selectedRoles,
  frequentRoles,
  error,
  onRolesChange,
  onDropShortcut,
}) {
  const toggleRole = useToggleRole(draftKey, selectedRoles, onRolesChange);

  // Anything already chosen shows as a selected pill, so it is dropped from the
  // shortcuts rather than appearing twice in the same strip.
  const quickAddRoles = useMemo(
    () => frequentRoles.filter(role => !selectedRoles.includes(role.id)),
    [frequentRoles, selectedRoles]
  );

  const sortedSelected = useMemo(() => [...selectedRoles].sort(), [selectedRoles]);

  if (!hasRoles) {
    return (
      <div className="uf-field">
        <p className="uf-hint uf-hint--error">
          No roles exist yet — create one under Roles, then come back here.
        </p>
      </div>
    );
  }

  return (
    <div className="uf-field">
      <div className="uf-roles-bar">
        <div className="uf-roles-bar__group">
          {sortedSelected.length === 0 ? (
            <span className="uf-roles-bar__empty">
              None chosen yet — use Quick add, or open the full roles catalog below.
            </span>
          ) : (
            sortedSelected.map(roleId => (
              <Pill
                key={roleId}
                label={roleId}
                variant="primary"
                size="xs"
                onClick={() => toggleRole(roleId)}
              />
            ))
          )}
        </div>
        {quickAddRoles.length > 0 && (
          <div className="uf-roles-bar__group uf-roles-bar__group--quick">
            <span className="uf-roles-bar__label">Quick add</span>
            {quickAddRoles.map(role => (
              <Pill
                key={role.id}
                label={role.id}
                variant="light"
                size="xs"
                onClick={() => toggleRole(role.id)}
                removable
                removeLabel="Remove shortcut for"
                onRemove={() => onDropShortcut(role.id)}
              />
            ))}
          </div>
        )}
      </div>
      {error && <p className="uf-hint uf-hint--error">{error}</p>}
    </div>
  );
});

export const UserRolesCatalog = React.memo(function UserRolesCatalog({
  draftKey,
  roles,
  selectedRoles,
  onRolesChange,
}) {
  const toggleRole = useToggleRole(draftKey, selectedRoles, onRolesChange);

  return (
    <div className="uf-field">
      <div className="uf-roles">
        <RolesGrid
          allRoles={roles}
          selectedRoleIds={selectedRoles}
          onToggle={toggleRole}
          showFrequent={false}
          categoryColumns={4}
          variant="cards"
        />
      </div>
    </div>
  );
});
